import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { useNavigate } from 'react-router-dom';
import { apiHelper } from '../../configuration/api/apiHelper';
import { getAllCategories } from '../../pages/Categories/actions';
import { connect } from 'react-redux';
import { HandymanAppState } from '../../configuration/redux/reducers';
import {
  CategoryViewModel,
  UserDetailsViewModel,
} from '../../configuration/api/apiSchema';
import { getAuthData } from '../../helpers/localStorageHelpers';
import { flattenCategories } from '../../pages/Categories/helper';
import {
  ButtonTertiary,
  ButtonTertiaryIcon,
  GlassButton,
  GlassColors,
} from '../glassmorphism/GlassmorphismStyles';
import { BottomBadges } from './BottomBadges';
import {
  getCategoryIconComponent,
  getCategoryIconUrl,
} from '../../helpers/getCategoryIcon';
import { ReactComponent as VarifiedIcon } from '../../assets/icons/bages/varify_small.svg';
// import { ReactComponent as EarlyBirdIcon } from '../../assets/icons/bages/early_bird.svg';
// import { ReactComponent as MostlyAppreciateIcon } from '../../assets/icons/bages/mostly_appreciated.svg';
import { ReactComponent as PhoneIcon } from '../../assets/icons/info/phone.svg';
import { ReactComponent as MailIcon } from '../../assets/icons/info/mail.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/info/location.svg';
import { useAppDispatch } from '../../configuration/redux/hooks';
import { createOrGetChat } from '../../pages/Orders/actions';
import { http } from '../../configuration/api/http';

// Map container styles
const mapContainerStyle = {
  width: '100%',
  height: '100%',
  maxHeight: '100%',
};

// Light minimalist map styles (matching the image)
const lightMinimalistStyles = [
  // Base
  {
    elementType: 'geometry',
    stylers: [{ color: '#d7dee4' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#eef0f2' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b7280' }],
  },

  // Localities
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4b5563' }],
  },

  // POI (very subtle)
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#e6e8eb' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7b8190' }],
  },

  // Parks / nature (almost gray-green)
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#9fd4b9' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6f7f77' }],
  },

  // Roads
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#aab3b9' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#d6dae0' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b7280' }],
  },

  // Highways
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#c6c6c6' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#909cb0' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#374151' }],
  },

  // Transit
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#e3e6ea' }],
  },

  // Water (very soft)
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#bdcce1' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7a8a99' }],
  },
  {
    featureType: 'poi.business',
    stylers: [{ visibility: 'off' }],
  },
];

// Dark mode map styles
const darkMapStyles = [
  // Base
  {
    elementType: 'geometry',
    stylers: [{ color: '#313131' }],
  },
  {
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#262626' }],
  },
  {
    elementType: 'labels.text.fill',
    stylers: [{ color: '#b3bac6' }],
  },

  // Cities / locality
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#e0e4ec' }],
  },

  // POI (almost invisible)
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#293542' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#8f96a3' }],
  },

  // Parks (very subtle green)
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [{ color: '#2e5a48' }],
  },
  {
    featureType: 'poi.park',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#7f8f88' }],
  },

  // Roads
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#4f4f4f' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#333d47' }],
  },
  {
    featureType: 'road',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#aeb5c2' }],
  },

  // Highways
  {
    featureType: 'road.highway',
    elementType: 'geometry',
    stylers: [{ color: '#4a4f57' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#333d47' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#f9fcfe' }],
  },

  // Transit
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#38424c' }],
  },

  // Water (almost gray)
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#212121' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6f7a89' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#1a1a1a' }],
  },
  {
    featureType: 'poi.business',
    stylers: [{ visibility: 'off' }],
  },
];

interface IpLocationResponse {
  ip?: string;
  city?: string;
  region?: string;
  country?: string;
  latitude: number;
  longitude: number;
  postal?: string;
  timezone?: string;
  org?: string;
}

interface HandymanViewModel {
  id: string;
  avatarUrl?: string;
  fullName: string;
  bio?: string;
  location?: string;
  phone?: string;
  email?: string;
  isVerified: boolean;
  categories?: Array<{
    categoryKey: string;
    hourlyRate?: number;
    fixedPriceRate?: number;
  }>;
  rating: number;
  reviewsCount: number;
  serviceAreaAddress?: string;
  serviceAreaJson?: string;
}

interface Handyman {
  id: string;
  name: string;
  specialty: string;
  rating: number;
  reviews: number;
  hourlyRate: string;
  fixedPriceRate?: string | null;
  phone: string;
  email: string;
  address: string;
  available: boolean;
  description: string;
  services: string[];
  coordinates: [number, number];
  avatarUrl?: string;
  isVerified: boolean;
}

interface StateFromProps {
  allCategories?: CategoryViewModel[];
  userDetails?: UserDetailsViewModel;
  isFetchingCategories: boolean;
  categoriesError: string | null;
}

interface DispatchFromProps {
  getAllCategories: () => void;
}

interface HandymanMapWidgetProps {
  radius?: number;
  address?: string;
  coordinates?: { lat: number; lng: number } | null;
  onRadiusChange?: (radius: number) => void;
  showControls?: boolean;
}

type Props = StateFromProps & DispatchFromProps & HandymanMapWidgetProps;

const authData = getAuthData();
const transformCategoryViewModel = (categories: CategoryViewModel[]): any[] => {
  return categories.map((cat) => ({
    key: cat.key || '',
    name: cat.name || '',
    subcategories: transformCategoryViewModel(cat.subcategories || []),
  }));
};

const normalizeAreaName = (name: string) =>
  name.replace(/\s+/g, ' ').replace(/,.*$/, '').trim();

function HandymanMapWidget(props: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const mapRef = useRef<google.maps.Map | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-maps-script',
    googleMapsApiKey: 'AIzaSyDmGs8jYs-PgU7PX8Fi5jmv1bPGdLXU0xA',
    libraries: ['places'],
  });

  const [selectedHandyman, setSelectedHandyman] = useState<Handyman | null>(
    null,
  );
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>({
    lat: 51.0958,
    lng: 0.0375,
  });

  const dispatch = useAppDispatch();
  const userId = props.userDetails?.id;
  const handleOpenChat = async () => {
    if (!userId || !selectedHandyman?.id) return;
    try {
      const chatId = await dispatch(
        createOrGetChat([userId, selectedHandyman.id]) as any,
      );
      if (chatId) {
        navigate(`/chats/${chatId}`);
      } else {
        console.error('Failed to get chat ID from response');
      }
    } catch (error) {
      console.error('Failed to open or create chat:', error);
    }
  };

  // ===== AREA LABEL (on map's bottom) =====
  const [areaLabel, setAreaLabel] = useState<string>('');
  const idleTimerRef = useRef<number | null>(null);

  const reverseGeocodeLabel = useCallback(
    (latLng: google.maps.LatLngLiteral): Promise<string | null> => {
      return new Promise((resolve) => {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status !== 'OK' || !results?.length) return resolve(null);

          const comps = results[0].address_components || [];
          const pick = (...types: string[]) =>
            comps.find((c) => types.some((t) => c.types.includes(t)))
              ?.long_name;

          const name =
            pick('neighborhood') ||
            pick('sublocality', 'sublocality_level_1') ||
            pick('locality', 'postal_town') ||
            pick('administrative_area_level_2') ||
            pick('administrative_area_level_1');

          resolve(name ? `${normalizeAreaName(name)} Area` : null);
        });
      });
    },
    [],
  );

  const placesAreaLabel = useCallback(
    (
      map: google.maps.Map,
      latLng: google.maps.LatLngLiteral,
    ): Promise<string | null> => {
      return new Promise((resolve) => {
        const service = new google.maps.places.PlacesService(map);

        service.nearbySearch(
          {
            location: latLng,
            radius: 1500, // 1.5km
          },
          (results, status) => {
            if (
              status !== google.maps.places.PlacesServiceStatus.OK ||
              !results?.length
            ) {
              return resolve(null);
            }

            const score = (r: google.maps.places.PlaceResult) => {
              const t = r.types || [];
              if (t.includes('neighborhood')) return 100;
              if (
                t.includes('sublocality') ||
                t.includes('sublocality_level_1')
              )
                return 90;
              if (t.includes('locality') || t.includes('postal_town'))
                return 80;
              if (t.includes('administrative_area_level_2')) return 70;
              if (t.includes('administrative_area_level_1')) return 60;
              return 0;
            };

            const best = [...results].sort((a, b) => score(b) - score(a))[0];
            const name = best?.name ? normalizeAreaName(best.name) : null;

            resolve(name ? `${name} Area` : null);
          },
        );
      });
    },
    [],
  );

  const updateAreaFromMap = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;

    const c = map.getCenter();
    if (!c) return;

    const center = { lat: c.lat(), lng: c.lng() };

    const fromPlaces = await placesAreaLabel(map, center);
    if (fromPlaces) {
      setAreaLabel(fromPlaces);
      return;
    }

    const fromGeo = await reverseGeocodeLabel(center);
    if (fromGeo) {
      setAreaLabel(fromGeo);
      return;
    }

    setAreaLabel('Selected Area');
  }, [placesAreaLabel, reverseGeocodeLabel]);

  const [userLocationCoords, setUserLocationCoords] =
    useState<google.maps.LatLngLiteral>({
      lat: 51.0958,
      lng: 0.0375,
    });
  const [mapZoom, setMapZoom] = useState(10); // Default zoom for 50km view (higher number = more zoomed in)
  const [handymen, setHandymen] = useState<Handyman[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<IpLocationResponse | null>(
    null,
  );

  const [hoverScaleById, setHoverScaleById] = useState<Record<string, number>>(
    {},
  );
  const animRef = useRef<Record<string, number>>({});
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const transformedCategories = useMemo(() => {
    return props.allCategories
      ? transformCategoryViewModel(props.allCategories)
      : [];
  }, [props.allCategories]);

  const categoryDictionary = useMemo(() => {
    return flattenCategories(transformedCategories);
  }, [transformedCategories]);

  // animation scale for marker on map
  const animateScale = (id: string, to: number, duration = 150) => {
    const from = hoverScaleById[id] ?? 1;
    const start = performance.now();

    if (animRef.current[id]) cancelAnimationFrame(animRef.current[id]);

    const step = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const value = from + (to - from) * eased;

      setHoverScaleById((prev) => ({ ...prev, [id]: value }));

      if (p < 1) animRef.current[id] = requestAnimationFrame(step);
    };

    animRef.current[id] = requestAnimationFrame(step);
  };

  const getCoordinatesFromGeoJson = (
    geoJson: string,
  ): [number, number] | null => {
    try {
      const parsed = JSON.parse(geoJson);
      if (parsed.type === 'Polygon' && parsed.coordinates?.[0]) {
        const coords = parsed.coordinates[0];
        let sumLat = 0,
          sumLng = 0;
        coords.forEach((coord: number[]) => {
          sumLng += coord[0];
          sumLat += coord[1];
        });
        return [sumLat / coords.length, sumLng / coords.length];
      }
    } catch (e) {
      console.error('Error parsing GeoJSON:', e);
    }
    return null;
  };

  const fetchHandymen = useCallback(
    async (fallbackCenter?: google.maps.LatLngLiteral) => {
      // Don't fetch if categories aren't loaded yet
      if (!categoryDictionary || Object.keys(categoryDictionary).length === 0) {
        console.log('⏳ Categories not loaded yet, skipping fetch');
        return;
      }

      try {
        console.log('🔍 Fetching handymen from API...');
        const hasCoords =
          typeof props.coordinates?.lat === 'number' &&
          typeof props.coordinates?.lng === 'number';
        const response = await apiHelper.post<HandymanViewModel[]>(
          '/browse-handyman',
          {
            page: 1,
            pageSize: 999,
            latitude: hasCoords ? props.coordinates!.lat : null,
            longitude: hasCoords ? props.coordinates!.lng : null,
            // Per backend precedence: coordinates win, address is the fallback.
            address: hasCoords ? null : props.address?.trim() || null,
            radiusKm: props.radius ?? null,
          },
        );

        console.log('📦 API response:', response);
        console.log('📊 Response data length:', response.data?.length || 0);

        if (response.data && response.data.length > 0) {
          const transformedHandymen: Handyman[] = response.data
            .filter((h) => {
              if (!h.serviceAreaJson) {
                console.log(`❌ Skipping ${h.fullName}: no serviceAreaJson`);
                return false;
              }
              const coords = getCoordinatesFromGeoJson(h.serviceAreaJson);
              if (!coords) {
                console.log(`❌ Skipping ${h.fullName}: invalid coordinates`);
                return false;
              }
              return true;
            })
            .map((handyman) => {
              const coordinates = getCoordinatesFromGeoJson(
                handyman.serviceAreaJson!,
              )!;
              const primaryCategoryKey =
                handyman.categories?.[0]?.categoryKey || 'general_services';
              const primaryCategoryName =
                categoryDictionary[primaryCategoryKey] || primaryCategoryKey;

              console.log(`✅ Handyman ${handyman.fullName}:`, {
                coordinates,
                specialty: primaryCategoryName,
                address: handyman.serviceAreaAddress,
              });

              return {
                id: handyman.id,
                name: handyman.fullName,
                specialty: primaryCategoryName,
                rating: handyman.rating,
                reviews: handyman.reviewsCount || 0,
                hourlyRate: handyman.categories?.[0]?.hourlyRate
                  ? `£${handyman.categories[0].hourlyRate}/hour`
                  : 'Rate not specified',
                fixedPriceRate: handyman.categories?.[0]?.fixedPriceRate
                  ? `£${handyman.categories[0].fixedPriceRate}`
                  : null,
                phone: handyman.phone || '',
                email: handyman.email || '',
                address:
                  handyman.serviceAreaAddress || 'Location not specified',
                available: Math.random() > 0.2,
                description: handyman.bio || 'Professional handyman',
                services: handyman.categories?.map(
                  (cat) =>
                    categoryDictionary[cat.categoryKey] || cat.categoryKey,
                ) || ['General Services'],
                coordinates,
                avatarUrl: handyman.avatarUrl,
                isVerified: handyman.isVerified,
              };
            });

          console.log(`✅ Transformed ${transformedHandymen.length} handymen`);
          console.log(
            '📍 Handymen coordinates:',
            transformedHandymen.map((h) => ({
              name: h.name,
              coords: h.coordinates,
            })),
          );
          setHandymen(transformedHandymen);
        } else {
          console.log('⚠️ No handymen data from API');
          setHandymen([]);
        }
      } catch (error) {
        console.error('❌ Error fetching handymen:', error);
        setHandymen([]);
      }
    },
    [categoryDictionary, props.coordinates, props.address, props.radius],
  );

  useEffect(() => {
    props.getAllCategories();
  }, [props.getAllCategories]);

  // Fetch handymen on mount and when categories are loaded
  useEffect(() => {
    console.log('🔄 useEffect triggered - categoryDictionary:', {
      hasDictionary: !!categoryDictionary,
      keysCount: categoryDictionary
        ? Object.keys(categoryDictionary).length
        : 0,
      isLoading,
    });

    if (
      categoryDictionary &&
      Object.keys(categoryDictionary).length > 0 &&
      !isLoading
    ) {
      console.log('✅ Categories loaded, fetching handymen...');
      const timer = setTimeout(() => fetchHandymen(), 500);
      return () => clearTimeout(timer);
    } else {
      console.log('⏸️ Waiting for categories to load or still loading...');
    }
  }, [categoryDictionary, fetchHandymen, isLoading]);

  useEffect(() => {
    if (!isLoaded) return;
    if (
      typeof props.coordinates?.lat === 'number' &&
      typeof props.coordinates?.lng === 'number'
    ) {
      const next = { lat: props.coordinates.lat, lng: props.coordinates.lng };
      setMapCenter(next);
      mapRef.current?.panTo(next);
      return;
    }
    if (!props.address?.trim()) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: props.address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        const next = { lat: loc.lat(), lng: loc.lng() };
        setMapCenter(next);
        mapRef.current?.panTo(next);
      }
    });
  }, [props.address, props.coordinates, isLoaded]);

  useEffect(() => {
    const getUserLocation = async () => {
      try {
        const response = await http('https://ipwho.is/');
        if (response.ok) {
          const data = await response.json();
          if (data && data.success !== false) {
            const locationData: IpLocationResponse = {
              ip: data.ip,
              city: data.city,
              region: data.region,
              country: data.country,
              latitude: data.latitude,
              longitude: data.longitude,
              timezone: data.timezone?.id,
              org: data.connection?.org,
            };
            setUserLocation(locationData);
            const newCenter: google.maps.LatLngLiteral = {
              lat: locationData.latitude,
              lng: locationData.longitude,
            };
            setUserLocationCoords(newCenter);
            // Don't clobber an explicitly picked location.
            if (
              !(
                typeof props.coordinates?.lat === 'number' &&
                typeof props.coordinates?.lng === 'number'
              )
            ) {
              setMapCenter(newCenter);
            }
            setMapZoom(10); // Set zoom to 10 for 50km view
            await fetchHandymen(newCenter);
          }
        }
      } catch (error) {
        console.error('Error getting user location:', error);
        const defaultLocation: google.maps.LatLngLiteral = {
          lat: 51.5074,
          lng: -0.1278,
        };
        setUserLocationCoords(defaultLocation);
        setMapCenter(defaultLocation);
        setMapZoom(10); // Set zoom to 10 for 50km view
        await fetchHandymen(defaultLocation);
      } finally {
        setIsLoading(false);
      }
    };
    getUserLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getZoomForRadius = (r: number): number => {
    if (r <= 2) return 16;
    if (r <= 5) return 15;
    if (r <= 10) return 13;
    if (r <= 20) return 11;
    if (r <= 50) return 9;
    if (r <= 100) return 8;
    return 7;
  };

  useEffect(() => {
    if (props.radius !== undefined && mapRef.current) {
      const desiredZoom = getZoomForRadius(props.radius);
      if (Math.abs(desiredZoom - mapZoom) > 0.5) {
        setMapZoom(desiredZoom);
        mapRef.current.setZoom(desiredZoom);
      }
    }
  }, [props.radius, mapZoom]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedHandyman(null);
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  const handleViewProfile = (handymanId: string) => {
    navigate(`/browse-handyman/${handymanId}`);
  };

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    setTimeout(() => {
      google.maps.event.trigger(map, 'resize');
      map.setCenter(mapCenter);
    }, 0);
    map.addListener('idle', () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);

      idleTimerRef.current = window.setTimeout(() => {
        updateAreaFromMap();
      }, 250);
    });

    const hasPickedCoords =
      typeof props.coordinates?.lat === 'number' &&
      typeof props.coordinates?.lng === 'number';

    if (hasPickedCoords) {
      map.setCenter({
        lat: props.coordinates!.lat,
        lng: props.coordinates!.lng,
      });
      map.setZoom(getZoomForRadius(props.radius ?? 10));
    } else if (userLocation) {
      map.setCenter(userLocationCoords);
      map.setZoom(10); // 50km view
    } else {
      map.setZoom(10);
    }

    updateAreaFromMap();
  };

  // Center on user location with 50km zoom when location is available
  useEffect(() => {
    if (userLocation && mapRef.current) {
      mapRef.current.setCenter(userLocationCoords);
      mapRef.current.setZoom(10); // 50km view - don't zoom out further
    }
  }, [userLocation, userLocationCoords]);

  const onMapClick = () => {
    setSelectedHandyman(null);
  };

  const handleGoToMyLocation = () => {
    if (mapRef.current) {
      mapRef.current.panTo(userLocationCoords);
      mapRef.current.setZoom(10); // Zoom for 50km view
    }
  };

  if (loadError) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '550px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color='error'>Failed to load Google Maps</Typography>
      </Box>
    );
  }

  if (!isLoaded || isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '550px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant='h6'>Loading map...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Box
        sx={{
          position: 'relative',
          borderRadius: '32px',
          overflow: 'hidden',
          height: '100%',
          // Soft neumorphic styling for minimalist map appearance
          boxShadow: `
                        4px 4px 12px rgba(0, 0, 0, 0.08),
                        -4px -4px 12px rgba(255, 255, 255, 0.6),
                        inset 1px 1px 3px rgba(255, 255, 255, 0.4),
                        inset -1px -1px 3px rgba(0, 0, 0, 0.05)
                      `,
        }}
      >
        <Box
          sx={{
            // height: '550px',
            height: { xs: '320px', sm: '100%' },
            borderRadius: '32px',
            overflow: 'hidden',
            boxShadow: `
              0px 0px 34.77px 0px #0000000D inset,
              0px -0.5px 0.25px 0px #00000080 inset,
              0px 0.5px 0.75px 0px #FFFFFF inset,
              0px 2.07px 3.73px 0px #0000004D,
              0px 2.08px 2.48px -2px #00000066,
              0px 3.08px 2px -2px #00000054
            `,
          }}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={onMapLoad}
            onIdle={updateAreaFromMap}
            onClick={onMapClick}
            options={{
              styles: isDark ? darkMapStyles : lightMinimalistStyles,
              disableDefaultUI: true,
              zoomControl: props.showControls !== false,
              mapTypeControl: false,
              scaleControl: true,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: true,
              gestureHandling: 'greedy', // Enables pinch-to-zoom on mobile
              mapTypeId: 'roadmap',
            }}
          >
            {/* User location marker */}

            {(() => {
              console.log('coords used in render', userLocationCoords);
              return null;
            })()}
            {userLocation && (
              <MarkerF
                position={userLocationCoords}
                icon={{
                  path: google.maps.SymbolPath.CIRCLE,
                  scale: 12,
                  fillColor: '#005CAD',
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 3,
                }}
              />
            )}

            {/* Handyman markers */}
            {(() => {
              if (handymen.length > 0) {
                console.log(`🗺️ Rendering ${handymen.length} markers on map`);
              }
              return null;
            })()}
            {handymen
              .map((handyman) => {
                ('');
                // const { color, icon } = getCategoryIcon(handyman.specialty, true);
                const isSelected = selectedHandyman?.id === handyman.id;
                const iconChar = handyman.specialty.charAt(0).toUpperCase();
                // Validate coordinates
                if (
                  !handyman.coordinates ||
                  handyman.coordinates.length !== 2
                ) {
                  console.error(
                    `❌ Invalid coordinates for ${handyman.name}:`,
                    handyman.coordinates,
                  );
                  return null;
                }

                const [lat, lng] = handyman.coordinates;
                if (
                  isNaN(lat) ||
                  isNaN(lng) ||
                  lat < -90 ||
                  lat > 90 ||
                  lng < -180 ||
                  lng > 180
                ) {
                  console.error(`❌ Invalid lat/lng for ${handyman.name}:`, {
                    lat,
                    lng,
                  });
                  return null;
                }

                // Use simple Google Maps symbol marker
                const baseSize = isSelected ? 56 : 44;
                const scale = hoverScaleById[handyman.id] ?? 1;
                const size = Math.round(baseSize * scale);

                return (
                  <MarkerF
                    key={handyman.id}
                    position={{ lat, lng }}
                    title={handyman.name}
                    onMouseOver={() => animateScale(handyman.id, 1.1)}
                    onMouseOut={() => animateScale(handyman.id, 1)}
                    zIndex={scale > 1 ? 9999 : undefined}
                    icon={{
                      url: getCategoryIconUrl(handyman.specialty),
                      scaledSize: new google.maps.Size(size, size),
                      anchor: new google.maps.Point(size / 2, size / 2),
                    }}
                    animation={
                      isSelected ? google.maps.Animation.BOUNCE : undefined
                    }
                    onClick={() => setSelectedHandyman(handyman)}
                  />
                );
              })
              .filter(Boolean)}
            {handymen.length === 0 && isLoaded && !isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1000,
                  p: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <Typography variant='body2' color='text.secondary'>
                  No handymen found in this area
                </Typography>
              </Box>
            )}
          </GoogleMap>
        </Box>

        {props.showControls !== false && (
          <Box
            sx={{
              position: 'absolute',
              top: 68,
              right: 14,
              zIndex: 1000,
            }}
          >
            <IconButton
              onClick={handleGoToMyLocation}
              sx={{
                backgroundColor: 'rgba(0, 92, 173, 0.9)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 4px 12px rgba(0, 92, 173, 0.3)',
                color: 'white',
                width: 40,
                height: 40,
                '&:hover': {
                  backgroundColor: 'rgba(0, 92, 173, 1)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MyLocationIcon />
            </IconButton>
          </Box>
        )}

        {/* Custom Info Window Overlay */}
        {selectedHandyman && (
          <Box
            sx={{
              position: 'absolute',
              top: { xs: 8, md: 16 },
              left: '50%',
              transform: 'translateX(-50%)',
              width: { xs: 'calc(100% - 32px)', sm: '90%', md: '513px' },
              maxWidth: { xs: 'calc(100% - 32px)', sm: '90%', md: '513px' },
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '12px', sm: '14px', md: '16px' },
              p: { xs: '16px', sm: '20px', md: '16px 24px 24px' },
              background: isDark ? 'rgba(0, 92, 173, 0.7)' : '#3B3B3B2E',
              backdropFilter: 'blur(12px)',
              borderRadius: { xs: '24px', sm: '28px', md: '32px' },
              boxShadow: isDark
                ? `0px 0px 16px 0px rgba(92, 92, 92, 0.3) inset,
              0px 0px 3px 0px rgba(255, 255, 255, 0.4) inset,
              -1px -1px 0.5px -1px rgba(255, 255, 255, 1) inset,
              1px 1px 0.5px -1px rgba(255, 255, 255, 0.4) inset,
              -1px -1px 0px -0.5px rgba(255, 255, 255, 0.5) inset,
              1px 1px 0px -0.5px rgba(255, 255, 255, 0.6) inset,
              0px 1px 8px 0px rgba(0, 0, 0, 0.12),
              0px 0px 2px 0px rgba(0, 0, 0, 0.1)`
                : `0px 0px 16px 0px #5C5C5C4D inset,
              0px 0px 3px 0px #FFFFFF66 inset,
              -1px -1px 0.5px -1px #FFFFFF inset,
              1px 1px 0.5px -1px #FFFFFF66 inset,
              -1px -1px 0px -0.5px #FFFFFF80 inset,
              1px 1px 0px -0.5px #FFFFFF99 inset,
              0px 1px 8px 0px #0000001F,
              0px 0px 2px 0px #0000001A`,
              maxHeight: {
                xs: 'calc(100% - 16px )',
                md: 'calc(100% - 32px )',
              },
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                background: 'transparent',
                width: '12px',
                zIndex: '-1',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
                marginBlock: '29px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderLeft: '3px solid transparent',
                borderRight: '3px solid transparent',
                background: 'rgba(59, 59, 59, 0.18)',
                borderRadius: '6px',
                height: '34px',
                backgroundClip: 'content-box',
                boxShadow: `inset 0 1px 1px rgba(255,255,255,0.45),
  inset 0 -1px 1px rgba(0, 0, 0, 0.48),
  inset 0 0px 2px #ffffffff,
  inset 1px 2px 4px rgba(0, 0, 0, 0.25)`,
              },
            }}
          >
            {/* Close button */}
            <ButtonTertiaryIcon
              onClick={() => setSelectedHandyman(null)}
              sx={{
                position: 'absolute',
                zIndex: 1,
                top: 16,
                right: { xs: 16, sm: 24 },
                width: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                '&&:hover': {
                  '&& path': {
                    fill: 'rgba(226, 247, 255, 1)',
                  },
                },
              }}
            >
              <CloseIcon
                sx={{
                  fontSize: { xs: 18, sm: 20, md: 22 },
                  '&& path': { fill: 'white' },
                }}
              />
            </ButtonTertiaryIcon>

            {/* Header with icon and specialty */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5 },
                pr: { xs: '45px', sm: 5 },
              }}
            >
              <Box
                sx={{
                  width: { xs: 56, sm: 64, md: 72 },
                  height: { xs: 56, sm: 64, md: 72 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  p: '8px',
                  backgroundColor: 'rgba(59, 59, 59, 0.18)',
                  boxShadow: isDark
                    ? `0px 0px 34.77px 0px rgba(0, 0, 0, 0.05) inset,
                  0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.5) inset,
                  0px 0.5px 0.75px 0px rgba(255, 255, 255, 1) inset,
                  0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.3),
                  0px 2.08px 2.48px -2px rgba(0, 0, 0, 0.4),
                  0px 3.08px 2px -2px rgba(0, 0, 0, 0.33)`
                    : `0px 0px 34.77px 0px rgba(0, 0, 0, 0.05) inset,
                  0px -0.5px 0.25px 0px rgba(0, 0, 0, 0.5) inset,
                  0px 0.5px 0.75px 0px rgba(255, 255, 255, 1) inset,
                  0px 2.07px 3.73px 0px rgba(0, 0, 0, 0.3),
                  0px 2.08px 2.48px -2px rgba(0, 0, 0, 0.4),
                  0px 3.08px 2px -2px rgba(0, 0, 0, 0.33)`,
                }}
              >
                {React.createElement(
                  getCategoryIconComponent(selectedHandyman.specialty),
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='h6'
                  sx={{
                    '&&': {
                      fontFamily: 'Montserrat',
                      fontWeight: 800,
                      fontSize: { xs: '18px', sm: '20px' },
                      lineHeight: '160%',
                      letterSpacing: '.15px',
                      color: isDark
                        ? GlassColors.accent
                        : GlassColors.secondary,
                    },
                  }}
                >
                  {selectedHandyman.specialty}
                </Typography>
              </Box>
            </Box>

            {/* Profile section */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <Avatar
                src={selectedHandyman.avatarUrl}
                sx={{
                  width: 40,
                  height: 40,
                  boxShadow: `
                  -2.82px -2.82px 8.49px 0px #FFFFFF4D,
                  14.15px 14.15px 22.64px -5.37px #0000001A,
                  -7.54px -7.54px 8.66px 0px #FFFFFF33,
                  5px 5px 24px -11.7px #00000033,
                `,
                }}
              >
                {selectedHandyman.name.charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    '& svg': { width: { xs: '12px', sm: '16px' } },
                    '& svg path': {
                      fill: isDark ? GlassColors.accent : GlassColors.secondary,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Montserrat',
                      fontWeight: 800,
                      fontSize: { xs: '14px', sm: '20px' },
                      lineHeight: '160%',
                      letterSpacing: '.15px',
                      color: isDark
                        ? GlassColors.dark.textPrimary
                        : GlassColors.light.textPrimary,
                    }}
                  >
                    {selectedHandyman.name}
                  </Typography>
                  {selectedHandyman.isVerified && <VarifiedIcon />}
                  {/* {selectedHandyman.isEarlyBird && <EarlyBirdIcon />}
                {selectedHandyman.MostlyAppreciate && <MostlyAppreciateIcon />} */}
                  {selectedHandyman.available && (
                    <Box
                      sx={{
                        fontSize: '12px',
                        lineHeight: '18px',
                        px: 2,
                        py: '2px',
                        backgroundColor: GlassColors.additional,
                        color: GlassColors.textPrimary,
                        borderRadius: '999px',
                        display: 'flex',
                        alignSelf: 'start',
                        alignItems: 'center',
                        ml: 'auto',
                        boxShadow: `
                        0px 0px 34.77px 0px #0000000D inset,
                        0px -0.5px 0.25px 0px #00000080 inset,
                        0px 0.5px 0.75px 0px #FFFFFF inset,
                        0px 2.07px 3.73px 0px #0000004D,
                        0px 2.08px 2.48px -2px #00000066,
                        0px 3.08px 2px -2px #00000054
                      `,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 600,
                          fontSize: { xs: '10px', sm: '11px', md: '12px' },
                          color: '#1A1A1A',
                        }}
                      >
                        Available now
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                {[1, 2, 3, 4, 5].map((star) => (
                  <StarIcon
                    key={star}
                    sx={{
                      fontSize: 18,
                      filter: 'drop-shadow(1px 2px 1px rgb(0 0 0 / 0.5))',
                      color:
                        star <= Math.floor(selectedHandyman.rating)
                          ? '#D1AB01'
                          : '#E5E5DC',
                    }}
                  />
                ))}
              </Box>

              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontWeight: 900,
                  fontSize: { xs: '10px', md: '12px' },
                  lineHeight: '166%',
                  color: isDark
                    ? GlassColors.dark.textSecondary
                    : GlassColors.light.textSecondary,
                  ml: 0.5,
                }}
              >
                ({selectedHandyman.rating.toFixed(1)}/5)
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: { xs: '12px', md: '14px' },
                  color: isDark
                    ? GlassColors.dark.textSecondary
                    : GlassColors.light.textSecondary,
                  ml: 0.5,
                }}
              >
                {selectedHandyman.reviews} reviews •{' '}
                {Math.round((selectedHandyman.rating / 5) * 100)}% positive
              </Typography>
            </Box>

            {/* Description */}
            <Typography
              sx={{
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: { xs: '14px', md: '16px' },
                letterSpacing: '.15px',
                color: isDark
                  ? GlassColors.dark.textPrimary
                  : GlassColors.light.textPrimary,
                lineHeight: 1.5,
              }}
            >
              {selectedHandyman.description}
            </Typography>

            {/* Service chips */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1, sm: 1.5 },
                flexWrap: 'wrap',
              }}
            >
              {selectedHandyman.services.slice(0, 3).map((service) => (
                <Chip
                  key={service}
                  label={service}
                  sx={{
                    '&&': {
                      px: { xs: 1.5, sm: 2 },
                      py: '5.5px',
                      backgroundColor: isDark
                        ? GlassColors.dark.primaryBackground
                        : GlassColors.light.primaryBackground,

                      backdropFilter: 'blur(12px)',
                      boxShadow: `
                    0px 0px 16px 0px #5C5C5C4D inset,
                    0px 0px 3px 0px #FFFFFF66 inset,
                    -1px -1px 0.5px -1px #FFFFFF inset,
                    1px 1px 0.5px -1px #FFFFFF66 inset,
                    -1px -1px 0px -0.5px #FFFFFF80 inset,
                    1px 1px 0px -0.5px #FFFFFF99 inset,
                    0px 1px 8px 0px #0000001F,
                    0px 0px 2px 0px #0000001A
                  `,
                      border: 0,
                      height: 'auto',
                      maxHeight: 'none',
                      borderRadius: '999px',
                    },

                    '& .MuiChip-label': {
                      fontFamily: 'Montserrat',
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      lineHeight: '29px',
                      fontWeight: 500,
                      color: isDark
                        ? GlassColors.dark.textPrimary
                        : GlassColors.light.textPrimary,
                      py: 0,
                    },
                  }}
                />
              ))}
            </Box>

            {/* Pricing */}
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontWeight: 700,
                lineHeight: '175%',
                letterSpacing: '.15px',
                fontSize: { xs: '14px', sm: '16px' },
                color: isDark ? GlassColors.accent : GlassColors.secondary,
              }}
            >
              {selectedHandyman.hourlyRate}
            </Typography>

            {authData && (
              <Box>
                {selectedHandyman.address && (
                  <Typography
                    sx={{
                      fontFamily: 'roboto, sans-serif',
                      fontSize: '16px',
                      lineHeight: 1.5,
                      letterSpacing: '.15px',
                      fontWeight: 400,
                      color: isDark
                        ? GlassColors.dark.textPrimary
                        : GlassColors.light.textPrimary,

                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      mb: '4px',

                      '& svg': {
                        mr: '4px',
                      },
                      '& svg path': {
                        fill: isDark
                          ? GlassColors.accent
                          : GlassColors.secondary,
                      },
                    }}
                  >
                    <LocationIcon />
                    {selectedHandyman.address}
                  </Typography>
                )}
                {selectedHandyman.email && (
                  <Typography
                    sx={{
                      fontFamily: 'roboto, sans-serif',
                      fontSize: '16px',
                      lineHeight: 1.5,
                      letterSpacing: '.15px',
                      fontWeight: 400,
                      color: isDark
                        ? GlassColors.dark.textPrimary
                        : GlassColors.light.textPrimary,

                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      mb: '4px',

                      '& svg': {
                        mr: '4px',
                      },
                      '& svg path': {
                        fill: isDark
                          ? GlassColors.accent
                          : GlassColors.secondary,
                      },
                    }}
                  >
                    <MailIcon />
                    {selectedHandyman.email}
                  </Typography>
                )}
                {selectedHandyman.phone && (
                  <Typography
                    sx={{
                      fontFamily: 'roboto, sans-serif',
                      fontSize: '16px',
                      lineHeight: 1.5,
                      letterSpacing: '.15px',
                      fontWeight: 400,
                      color: isDark
                        ? GlassColors.dark.textPrimary
                        : GlassColors.light.textPrimary,

                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      mb: '4px',

                      '& svg': {
                        mr: '4px',
                      },
                      '& svg path': {
                        fill: isDark
                          ? GlassColors.accent
                          : GlassColors.secondary,
                      },
                    }}
                  >
                    <PhoneIcon />
                    {selectedHandyman.phone}
                  </Typography>
                )}
              </Box>
            )}

            {/* Action buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0.75, sm: 1 },
                justifyContent: 'space-between',
              }}
            >
              <ButtonTertiary
                variant='outlined'
                onClick={() => {
                  if (!authData) {
                    navigate('/sign-in');
                    return;
                  } else {
                    handleOpenChat();
                  }

                  // handleViewProfile(selectedHandyman.id);
                }}
                sx={{
                  '&&': {
                    py: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '12px', sm: '16px' },
                    lineHeight: 2,
                    background: 'rgba(132, 132, 132, 0.3)',
                    '&:hover': {
                      color: GlassColors.accent,
                    },
                  },
                }}
              >
                {authData ? `Contact` : `Log in to contact`}
              </ButtonTertiary>
              <GlassButton
                variant='contained'
                onClick={() => handleViewProfile(selectedHandyman.id)}
                sx={{
                  py: { xs: 0.75, sm: 1 },
                  fontSize: { xs: '12px', sm: '16px' },
                  lineHeight: 2,
                }}
              >
                View Profile
              </GlassButton>
            </Box>
          </Box>
        )}
      </Box>
      <BottomBadges
        total={handymen.length}
        available={handymen.filter((h) => h.available).length}
        areaLabel={areaLabel || 'Selected Area'}
        isDark={isDark}
      />
    </Box>
  );
}

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
  userDetails: state.user.userDetails,
  allCategories: state.category.allCategories,
  isFetchingCategories: state.category.isLoading,
  categoriesError: state.category.isError ? 'Failed to load categories' : null,
});

export default connect<StateFromProps, DispatchFromProps, {}, HandymanAppState>(
  mapStateToProps,
  {
    getAllCategories,
  },
)(HandymanMapWidget);
